import { Action, Thunk, action, thunk } from 'easy-peasy'
import {
  DEFAULT_FEATURE_FLAGS,
  fetchClientFeatureFlags,
  FeatureFlagValues,
  mergeWithDefaults,
} from '@mortgage-pos/data'
import { environment } from '@mortgage-pos/ui/env'
import rollbar from '@mortgage-pos/ui/services/rollbar'
import { CustomError } from '@mortgage-pos/types'

export interface FeatureFlagsModel {
  values: FeatureFlagValues
  loaded: boolean
  setValues: Action<FeatureFlagsModel, FeatureFlagValues>
  setLoaded: Action<FeatureFlagsModel, boolean>
  load: Thunk<FeatureFlagsModel>
}

const featureFlags = (): FeatureFlagsModel => ({
  // Seed with defaults so reads never see `undefined` before the fetch resolves.
  values: { ...DEFAULT_FEATURE_FLAGS },
  loaded: false,

  setValues: action((state, payload) => {
    state.values = payload
  }),

  setLoaded: action((state, payload) => {
    state.loaded = payload
  }),

  load: thunk(async (actions) => {
    try {
      const fetched = await fetchClientFeatureFlags(environment.apiBaseUrl)

      actions.setValues(mergeWithDefaults(fetched))
      actions.setLoaded(true)
    } catch (error) {
      // Keep the seeded defaults — no blank state.
      const cause =
        error instanceof Error
          ? { name: error.name, message: error.message }
          : { name: 'UnknownError', message: String(error) }
      const statusCode = error instanceof CustomError ? error.statusCode : null

      const errorObject = new CustomError(
        'FeatureFlagsFetchError',
        'Failed to fetch client feature flags',
        statusCode
      )

      if (statusCode != null) {
        // Server failure — real backend problem.
        rollbar.error(errorObject, { cause, statusCode })
      } else {
        // Fetch never completed — expected browser noise.
        rollbar.warn(errorObject, { cause })
      }

      actions.setLoaded(true)
    }
  }),
})

export default featureFlags
